import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { User } from 'firebase/auth'
import { loadAuth } from '../lib/authClient'
import { isAdminEmail } from '../lib/admin'
import { setAnalyticsUser } from '../lib/analytics'

interface AuthContextValue {
  user: User | null
  loading: boolean
  isAdmin: boolean
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  isAdmin: false,
  signOut: async () => {},
  refreshUser: async () => {},
})

// Routes that cannot render anything useful until auth has resolved. On these
// the SDK loads immediately; everywhere else it waits for the browser to go
// idle so the public page's first paint and LCP are not competing with
// ~100 KB of firebase/auth parse and execute.
const AUTH_FIRST_PREFIXES = ['/portal', '/dashboard', '/login', '/auth', '/welcome', '/thank-you', '/waiver']

function needsAuthImmediately(pathname: string): boolean {
  return AUTH_FIRST_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

function scheduleAuthLoad(run: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  if (needsAuthImmediately(window.location.pathname)) {
    run()
    return () => {}
  }
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(run, { timeout: 2500 })
    return () => window.cancelIdleCallback(id)
  }
  const id = window.setTimeout(run, 1200)
  return () => window.clearTimeout(id)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [claims, setClaims] = useState<Record<string, unknown> | null>(null)
  // Bumped after a local profile mutation (e.g. updateProfile) so the provider
  // re-renders and consumers see the freshly reloaded user without a full reload.
  const [, setAuthNonce] = useState(0)

  const handleSignOut = useCallback(async () => {
    try {
      const { auth, mod } = await loadAuth()
      await mod.signOut(auth)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Sign out failed', err)
      throw err
    }
  }, [])

  // Pull the latest profile fields onto auth.currentUser and push them into
  // state. updateProfile alone mutates currentUser in place but never fires
  // onIdTokenChanged, so without this the context would stay stale until reload.
  const refreshUser = useCallback(async () => {
    const { auth } = await loadAuth()
    if (auth.currentUser) {
      await auth.currentUser.reload()
      setUser(auth.currentUser)
      setAuthNonce((n) => n + 1)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    let unsubscribe: (() => void) | null = null

    const subscribe = () => {
      loadAuth()
        .then(({ auth, mod }) => {
          if (cancelled) return
          // onIdTokenChanged fires on auth state changes AND on token refreshes,
          // which is when admin custom claims update (e.g., after sign-out/sign-in
          // following a setAdminClaim grant). onAuthStateChanged would miss the
          // token refresh path.
          unsubscribe = mod.onIdTokenChanged(auth, async (nextUser) => {
            setUser(nextUser)
            // Tags GA events as internal for staff accounts; members stay untagged.
            setAnalyticsUser(nextUser?.email ?? null)
            if (nextUser) {
              try {
                const tokenResult = await nextUser.getIdTokenResult()
                setClaims(tokenResult.claims as Record<string, unknown>)
              } catch (err) {
                // eslint-disable-next-line no-console
                console.error('AuthContext: failed to read ID token claims', err)
                setClaims(null)
              }
            } else {
              setClaims(null)
            }
            setLoading(false)
          })
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('AuthContext: failed to load firebase/auth', err)
          if (!cancelled) setLoading(false)
        })
    }

    const cancelScheduled = scheduleAuthLoad(subscribe)
    return () => {
      cancelled = true
      cancelScheduled()
      unsubscribe?.()
    }
  }, [])

  // Bridge mode (F49 Phase 2): admin if either the custom claim is set OR the
  // legacy email allowlist contains the user. Phase 3 will drop the email
  // fallback once all admins have the claim.
  const isAdmin = claims?.admin === true || isAdminEmail(user?.email ?? null)

  return (
    <AuthContext.Provider value={{ user, loading, isAdmin, signOut: handleSignOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
